// components/Auth/UserMenu.jsx
'use client'
import { useEffect, useRef, useState } from 'react'
import LinkComponent from '@components/LinkComponent'
import { useUser } from '@context/UserContext'

const DEFAULT_LABELS = {
  en: { login: 'Log in', logout: 'Log out', profile: 'My profile' },
  th: { login: 'เข้าสู่ระบบ', logout: 'ออกจากระบบ', profile: 'โปรไฟล์ของฉัน' },
}

function Avatar({ user, size = 30, color }) {
  const [broken, setBroken] = useState(false)
  const initial = (user?.firstName || user?.name || user?.email || '?').trim().charAt(0).toUpperCase()
  if (user?.image && !broken) {
    // Plain <img>: avatar hosts vary (Google, Facebook) and Google rejects
    // requests that send a referrer.
    return (
      <img src={user.image} alt="" width={size} height={size} referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    )
  }
  return (
    <span aria-hidden="true" style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: `color-mix(in srgb, ${color} 15%, transparent)`, color,
      fontSize: size * 0.45, fontWeight: 700,
    }}>{initial}</span>
  )
}

/**
 * variant="desktop" → login button / avatar with dropdown (navbar)
 * variant="drawer"  → full-width row for the mobile drawer footer
 */
export default function UserMenu({
  variant = 'desktop', lang = 'en', linkColor = '#374151', hoverColor = '#019da4',
  profileUrl, labels,
}) {
  const { user, isLoading, isLoggedIn, openLogin, logout } = useUser()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const t = { ...DEFAULT_LABELS.en, ...DEFAULT_LABELS[lang], ...labels }

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  // Reserve the space while loading so the navbar doesn't shift
  if (isLoading) return <span style={{ width: variant === 'drawer' ? '100%' : 30, height: 30 }} />

  if (!isLoggedIn) {
    return (
      <button onClick={() => openLogin({ lang })} style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: variant === 'drawer' ? '100%' : 'auto',
        fontSize: '0.875rem', fontWeight: 600, color: hoverColor,
        background: 'transparent', border: `1.5px solid ${hoverColor}`,
        borderRadius: 6, padding: '6px 16px', cursor: 'pointer', whiteSpace: 'nowrap',
      }}>
        {t.login}
      </button>
    )
  }

  const displayName = user.firstName || user.name || user.email

  if (variant === 'drawer') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
        <Avatar user={user} color={hoverColor} size={34} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: linkColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          {profileUrl && (
            <LinkComponent href={profileUrl} style={{ fontSize: '0.75rem', color: hoverColor, textDecoration: 'none' }}>{t.profile}</LinkComponent>
          )}
        </div>
        <button onClick={() => logout()} style={{ fontSize: '0.8125rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          {t.logout}
        </button>
      </div>
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={displayName}
        style={{ display: 'inline-flex', background: 'none', border: 'none', padding: 0, cursor: 'pointer', borderRadius: '50%' }}>
        <Avatar user={user} color={hoverColor} />
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 10px)', insetInlineEnd: 0, zIndex: 1000,
          minWidth: 220, background: '#fff', borderRadius: 8, padding: '6px 0',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', animation: 'navDropFadeIn 0.12s ease',
        }}>
          <div style={{ padding: '8px 16px 10px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>{user.name ?? displayName}</div>
            {user.email && <div style={{ fontSize: '0.75rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</div>}
          </div>
          {profileUrl && (
            <LinkComponent href={profileUrl} role="menuitem" onClick={() => setOpen(false)}
              style={{ display: 'block', padding: '9px 16px', fontSize: '0.8125rem', color: '#374151', textDecoration: 'none' }}>
              {t.profile}
            </LinkComponent>
          )}
          <button role="menuitem" onClick={() => logout()} style={{
            display: 'block', width: '100%', textAlign: 'start', padding: '9px 16px',
            fontSize: '0.8125rem', color: '#374151', background: 'none', border: 'none', cursor: 'pointer',
          }}>
            {t.logout}
          </button>
        </div>
      )}
    </div>
  )
}